<template>
  <div id="layout-wrapper">
    <NavigationBar />

    <div id="content" class="content-minimize">
      <div class="page-content">
        <div class="container-fluid">

          <div>
            <!-- Page header -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h4 class="mb-sm-0 font-size-18">Employee Warnings</h4>
                  <div class="page-title-right">
                    <ol class="breadcrumb m-0">
                      <li class="breadcrumb-item"><a href="index.html">Home</a></li>
                      <li class="breadcrumb-item active">HR Management</li>
                      <li class="breadcrumb-item active">Warnings</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>

            <!-- Filters and controls section -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-body">
                    <div class="d-flex flex-wrap align-items-center mb-3">
                      <h5 class="card-title me-3 mb-3 mb-md-0">Warning Filters</h5>
                      <div class="ms-auto">
                        <button @click="createWarning" class="btn btn-primary me-2">
                          <i class="mdi mdi-plus me-1"></i> Issue Warning
                        </button>
                        <button @click="refreshData" class="btn btn-secondary me-2">
                          <i class="mdi mdi-refresh me-1"></i> Refresh
                        </button>
                        <button @click="exportWarnings" class="btn btn-success">
                          <i class="mdi mdi-file-export me-1"></i> Export
                        </button>
                      </div>
                    </div>

                    <div class="row g-3">
                      <div class="col-md-3">
                        <label class="form-label">Warning Type</label>
                        <select v-model="filters.warningType" class="form-select">
                          <option value="">All Warning Types</option>
                          <option v-for="type in warningTypes" :key="type" :value="type">
                            {{ type }}
                          </option>
                        </select>
                      </div>

                      <div class="col-md-3">
                        <label class="form-label">Severity</label>
                        <select v-model="filters.severity" class="form-select">
                          <option value="">All Severities</option>
                          <option value="High">High</option>
                          <option value="Medium">Medium</option>
                          <option value="Low">Low</option>
                        </select>
                      </div>

                      <div class="col-md-3">
                        <label class="form-label">Status</label>
                        <select v-model="filters.status" class="form-select">
                          <option value="">All Statuses</option>
                          <option value="Active">Active</option>
                          <option value="Resolved">Resolved</option>
                          <option value="Expired">Expired</option>
                        </select>
                      </div>

                      <div class="col-md-3">
                        <label class="form-label">Department</label>
                        <select v-model="filters.departmentId" class="form-select">
                          <option value="">All Departments</option>
                          <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                            {{ dept.departmentName }}
                          </option>
                        </select>
                      </div>

                      <div class="col-md-6">
                        <label class="form-label">Date Range</label>
                        <div class="input-group">
                          <input v-model="filters.startDate" type="date" class="form-control">
                          <span class="input-group-text">to</span>
                          <input v-model="filters.endDate" type="date" class="form-control">
                        </div>
                      </div>

                      <div class="col-md-6">
                        <label class="form-label">Search</label>
                        <div class="position-relative">
                          <input
                              type="text"
                              v-model="filters.search"
                              class="form-control"
                              placeholder="Search by employee, warning code..."
                          >
                          <i class="bx bx-search-alt search-icon position-absolute top-50 end-0 translate-middle-y me-2"></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Statistics cards -->
            <div class="row">
              <!-- Total Warnings -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card border-left-primary h-100 py-2">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0 me-3">
                        <div class="avatar-sm">
                          <span class="avatar-title bg-primary rounded-circle text-white font-size-20">
                            <i class="mdi mdi-alert-circle font-size-24"></i>
                          </span>
                        </div>
                      </div>
                      <div class="flex-grow-1">
                        <h6 class="font-size-15 mb-1">Total Warnings</h6>
                        <h4 class="mb-0">{{ metrics.totalWarnings || 0 }}</h4>
                        <span class="text-muted font-size-12">
                          All time
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Active Warnings -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card border-left-warning h-100 py-2">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0 me-3">
                        <div class="avatar-sm">
                          <span class="avatar-title bg-warning rounded-circle text-white font-size-20">
                            <i class="mdi mdi-alert font-size-24"></i>
                          </span>
                        </div>
                      </div>
                      <div class="flex-grow-1">
                        <h6 class="font-size-15 mb-1">Active Warnings</h6>
                        <h4 class="mb-0">{{ metrics.activeWarnings || 0 }}</h4>
                        <span class="text-warning font-size-12">Currently in effect</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- High Severity -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card border-left-danger h-100 py-2">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0 me-3">
                        <div class="avatar-sm">
                          <span class="avatar-title bg-danger rounded-circle text-white font-size-20">
                            <i class="mdi mdi-alert-octagon font-size-24"></i>
                          </span>
                        </div>
                      </div>
                      <div class="flex-grow-1">
                        <h6 class="font-size-15 mb-1">High Severity</h6>
                        <h4 class="mb-0">{{ metrics.highSeverity || 0 }}</h4>
                        <span class="text-danger font-size-12">Critical issues</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Pending Acknowledgment -->
              <div class="col-xl-3 col-md-6 mb-4">
                <div class="card border-left-info h-100 py-2">
                  <div class="card-body">
                    <div class="d-flex align-items-center">
                      <div class="flex-shrink-0 me-3">
                        <div class="avatar-sm">
                          <span class="avatar-title bg-info rounded-circle text-white font-size-20">
                            <i class="mdi mdi-clock-alert font-size-24"></i>
                          </span>
                        </div>
                      </div>
                      <div class="flex-grow-1">
                        <h6 class="font-size-15 mb-1">Pending Acknowledgment</h6>
                        <h4 class="mb-0">{{ metrics.pendingAcknowledgment || 0 }}</h4>
                        <span class="text-info font-size-12">Needs employee action</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Warning Type Breakdown and Recent Warnings -->
            <div class="row">
              <!-- Warning Type Breakdown -->
              <div class="col-xl-6 col-lg-6 mb-4">
                <div class="card h-100">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">Warnings by Type</h5>
                  </div>
                  <div class="card-body">
                    <div v-if="metrics.typeCounts && Object.keys(metrics.typeCounts).length > 0" class="chart-container" style="position: relative; height:250px; width:100%">
                      <div class="table-responsive">
                        <table class="table table-sm table-bordered">
                          <thead class="table-light">
                          <tr>
                            <th>Warning Type</th>
                            <th class="text-center">Count</th>
                            <th class="text-center">Percentage</th>
                          </tr>
                          </thead>
                          <tbody>
                          <tr v-for="(count, type) in metrics.typeCounts" :key="type">
                            <td>{{ type }}</td>
                            <td class="text-center">{{ count }}</td>
                            <td class="text-center">
                              <div class="progress" style="height: 6px;">
                                <div
                                    class="progress-bar"
                                    :class="getWarningTypeColor(type)"
                                    role="progressbar"
                                    :style="`width: ${getWarningTypePercentage(type)}%`"
                                    :aria-valuenow="getWarningTypePercentage(type)"
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                ></div>
                              </div>
                              {{ getWarningTypePercentage(type) }}%
                            </td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div v-else class="text-center p-4">
                      <i class="mdi mdi-chart-bar font-size-48 text-muted"></i>
                      <p class="mt-2">No warning type data available</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Recent Warnings List -->
              <div class="col-xl-6 col-lg-6 mb-4">
                <div class="card h-100">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title mb-0">Recent Warnings</h5>
                  </div>
                  <div class="card-body">
                    <div v-if="recentWarnings.length > 0" class="recent-warnings-list">
                      <div v-for="warning in recentWarnings" :key="warning.id" class="recent-warning-item mb-3">
                        <div class="d-flex align-items-center p-3 border rounded">
                          <div class="warning-date-badge me-3 text-center">
                            <span class="date-month">{{ formatDateMonth(warning.warning_date) }}</span>
                            <span class="date-day fs-4 fw-bold d-block">{{ formatDateDay(warning.warning_date) }}</span>
                          </div>
                          <div class="warning-details flex-grow-1">
                            <h6 class="warning-title mb-1">{{ warning.employee_name }}</h6>
                            <div class="warning-meta text-muted small">
                              <span><i class="mdi mdi-alert-circle-outline me-1"></i>{{ warning.warning_type }}</span>
                              <span class="ms-2"><i class="mdi mdi-office-building me-1"></i>{{ warning.department_name || 'N/A' }}</span>
                            </div>
                          </div>
                          <div class="warning-type-badge ms-2">
                            <span class="badge rounded-pill" :class="getSeverityBadgeClass(warning.severity)">{{ warning.severity }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-center p-4">
                      <i class="mdi mdi-alert-circle-check font-size-48 text-muted"></i>
                      <p class="mt-2">No recent warnings issued</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Warnings List Table -->
            <div class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-transparent">
                    <h5 class="card-title">Warnings List</h5>
                  </div>
                  <div class="card-body">
                    <!-- Loading overlay -->
                    <div v-if="loading" class="text-center p-5">
                      <div class="spinner-border text-primary me-2" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                      <p class="mt-2">Loading warnings data...</p>
                    </div>

                    <!-- Empty state -->
                    <div v-else-if="!warnings.length" class="text-center p-5">
                      <i class="mdi mdi-alert-circle-outline font-size-48 text-muted"></i>
                      <h5 class="mt-2">No warnings found</h5>
                      <p class="text-muted">Try adjusting your filters or issue a new warning</p>
                      <button @click="resetFilters" class="btn btn-sm btn-outline-primary mt-2">Reset Filters</button>
                    </div>

                    <!-- Warnings table -->
                    <div v-else class="table-responsive">
                      <table class="table table-hover table-bordered align-middle mb-0">
                        <thead class="table-light">
                        <tr>
                          <th>Employee</th>
                          <th>Warning Details</th>
                          <th>Dates</th>
                          <th>Status</th>
                          <th class="text-center">Actions</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="warning in warnings" :key="warning.id">
                          <td>
                            <div class="d-flex align-items-center">
                              <div class="avatar-xs me-2 bg-light rounded p-1 text-center">
                                <i class="mdi mdi-account font-size-18 text-primary"></i>
                              </div>
                              <div>
                                <h5 class="font-size-14 mb-0">{{ warning.employee_name }}</h5>
                                <p class="text-muted mb-0 font-size-12">{{ warning.warning_code }}</p>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div class="d-flex flex-column">
                              <div>
                                <span class="badge bg-light text-dark me-1">Type:</span>
                                <span>{{ warning.warning_type }}</span>
                              </div>
                              <div class="mt-1">
                                <span class="badge bg-light text-dark me-1">Severity:</span>
                                <span class="badge" :class="getSeverityBadgeClass(warning.severity)">{{ warning.severity }}</span>
                              </div>
                              <div class="mt-1" v-if="warning.department_name">
                                <span class="badge bg-light text-dark me-1">Dept:</span>
                                <span>{{ warning.department_name }}</span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div>
                              <span class="d-block"><i class="mdi mdi-calendar-plus me-1"></i>Issued: {{ formatDate(warning.warning_date) }}</span>
                              <span class="d-block mt-1" v-if="warning.incident_date"><i class="mdi mdi-calendar-alert me-1"></i>Incident: {{ formatDate(warning.incident_date) }}</span>
                              <span class="d-block mt-1" v-if="warning.expiry_date"><i class="mdi mdi-calendar-remove me-1"></i>Expires: {{ formatDate(warning.expiry_date) }}</span>
                            </div>
                          </td>
                          <td>
                            <div class="d-flex flex-column">
                              <span class="badge" :class="getStatusBadgeClass(warning.status)">
                                {{ warning.status }}
                              </span>
                              <span v-if="warning.acknowledgment_required" class="mt-1 badge" :class="warning.acknowledged ? 'bg-success' : 'bg-warning'">
                                {{ warning.acknowledged ? 'Acknowledged' : 'Pending Acknowledgment' }}
                              </span>
                            </div>
                          </td>
                          <td class="text-center">
                            <div class="d-flex justify-content-center">
                              <button
                                  @click="viewWarningDetails(warning)"
                                  class="btn btn-sm btn-info me-2"
                                  title="View Details"
                              >
                                <i class="mdi mdi-eye"></i>
                              </button>
                              <button
                                  v-if="warning.status === 'Active' && canEditWarning(warning)"
                                  @click="editWarning(warning)"
                                  class="btn btn-sm btn-primary me-2"
                                  title="Edit Warning"
                              >
                                <i class="mdi mdi-pencil"></i>
                              </button>
                              <button
                                  v-if="warning.status === 'Active' && canAcknowledgeWarning(warning)"
                                  @click="acknowledgeWarning(warning)"
                                  class="btn btn-sm btn-success me-2"
                                  title="Acknowledge Warning"
                              >
                                <i class="mdi mdi-check"></i>
                              </button>
                              <button
                                  v-if="warning.status === 'Active' && canResolveWarning(warning)"
                                  @click="resolveWarning(warning)"
                                  class="btn btn-sm btn-success"
                                  title="Resolve Warning"
                              >
                                <i class="mdi mdi-check-all"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                        </tbody>
                      </table>
                    </div>

                    <!-- Pagination -->
                    <div v-if="warnings.length" class="d-flex justify-content-between align-items-center mt-4">
                      <div>
                        Showing <span class="fw-medium">{{ warnings.length }}</span> of
                        <span class="fw-medium">{{ pagination.totalWarnings }}</span> warnings
                      </div>
                      <nav v-if="pagination.totalPages > 1">
                        <ul class="pagination pagination-rounded mb-0">
                          <li class="page-item" :class="{ disabled: pagination.currentPage <= 1 }">
                            <a class="page-link" href="javascript:void(0)" @click="changePage(pagination.currentPage - 1)">
                              <i class="mdi mdi-chevron-left"></i>
                            </a>
                          </li>
                          <li
                              v-for="page in getPageNumbers()"
                              :key="page"
                              class="page-item"
                              :class="{ active: page === pagination.currentPage, disabled: page === '...' }"
                          >
                            <a v-if="page !== '...'" class="page-link" href="javascript:void(0)" @click="changePage(page)">
                              {{ page }}
                            </a>
                            <span v-else class="page-link">{{ page }}</span>
                          </li>
                          <li class="page-item" :class="{ disabled: pagination.currentPage >= pagination.totalPages }">
                            <a class="page-link" href="javascript:void(0)" @click="changePage(pagination.currentPage + 1)">
                              <i class="mdi mdi-chevron-right"></i>
                            </a>
                          </li>
                        </ul>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Warning Create/Edit Modal -->
  <div class="modal fade" id="warningModal" tabindex="-1" aria-labelledby="warningModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="warningModalLabel">{{ isEditMode ? 'Edit Warning' : 'Issue New Warning' }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="submitWarning">
            <div class="row g-3">
              <!-- Employee Selection (when creating) -->
              <div v-if="!isEditMode" class="col-md-12">
                <label class="form-label">Select Employee <span class="text-danger">*</span></label>
                <select v-model="warningForm.employeeId" class="form-select" required>
                  <option value="" disabled>Select Employee</option>
                  <option v-for="employee in employees" :key="employee.id" :value="employee.id">
                    {{ employee.firstName }} {{ employee.lastName }} - {{ employee.designation }} ({{ employee.departmentName }})
                  </option>
                </select>
              </div>

              <!-- Employee details (when selected or editing) -->
              <div v-if="selectedEmployee" class="col-md-12">
                <div class="card bg-light">
                  <div class="card-body p-3">
                    <h6 class="card-title mb-2">Employee Information</h6>
                    <div class="row">
                      <div class="col-md-6">
                        <p class="mb-1"><strong>Name:</strong> {{ selectedEmployee.firstName }} {{ selectedEmployee.lastName }}</p>
                        <p class="mb-1"><strong>Position:</strong> {{ selectedEmployee.designation }}</p>
                        <p class="mb-1"><strong>Department:</strong> {{ selectedEmployee.departmentName }}</p>
                      </div>
                      <div class="col-md-6">
                        <p class="mb-1"><strong>Employee ID:</strong> {{ selectedEmployee.empNo || selectedEmployee.nationalId }}</p>
                        <p class="mb-1"><strong>Email:</strong> {{ selectedEmployee.email }}</p>
                        <p class="mb-1"><strong>Phone:</strong> {{ selectedEmployee.phone }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <label class="form-label">Warning Type <span class="text-danger">*</span></label>
                <select v-model="warningForm.warningType" class="form-select" required>
                  <option value="" disabled>Select Warning Type</option>
                  <option v-for="type in warningTypes" :key="type" :value="type">{{ type }}</option>
                </select>
              </div>

              <div class="col-md-6">
                <label class="form-label">Severity <span class="text-danger">*</span></label>
                <select v-model="warningForm.severity" class="form-select" required>
                  <option value="" disabled>Select Severity</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>

              <div class="col-md-6">
                <label class="form-label">Warning Date <span class="text-danger">*</span></label>
                <input v-model="warningForm.warningDate" type="date" class="form-control" required>
              </div>

              <div class="col-md-6">
                <label class="form-label">Incident Date <span class="text-danger">*</span></label>
                <input v-model="warningForm.incidentDate" type="date" class="form-control" required>
              </div>

              <div class="col-md-12">
                <label class="form-label">Description of Offense <span class="text-danger">*</span></label>
                <textarea v-model="warningForm.description" class="form-control" rows="4" required></textarea>
                <small class="text-muted">
                  Provide a detailed description of the incident, including what occurred, when, where, and who was involved.
                </small>
              </div>

              <div class="col-md-12">
                <label class="form-label">Recommended Action</label>
                <textarea v-model="warningForm.recommendedAction" class="form-control" rows="3"></textarea>
                <small class="text-muted">
                  Specify any corrective actions or behavioral changes expected from the employee.
                </small>
              </div>

              <div class="col-md-6">
                <label class="form-label">Next Review Date</label>
                <input v-model="warningForm.nextReviewDate" type="date" class="form-control">
                <small class="text-muted">Date when the warning will be reviewed.</small>
              </div>

              <div class="col-md-6">
                <label class="form-label">Expiry Date</label>
                <input v-model="warningForm.expiryDate" type="date" class="form-control">
                <small class="text-muted">Date when the warning will no longer be in effect.</small>
              </div>

              <div class="col-md-6">
                <div class="form-check form-switch mt-3">
                  <input
                      v-model="warningForm.acknowledgmentRequired"
                      class="form-check-input"
                      type="checkbox"
                      id="acknowledgmentRequiredSwitch"
                  >
                  <label class="form-check-label" for="acknowledgmentRequiredSwitch">
                    Employee acknowledgment required
                  </label>
                </div>
              </div>

              <div class="col-md-12">
                <label class="form-label">Additional Comments</label>
                <textarea v-model="warningForm.comments" class="form-control" rows="2"></textarea>
              </div>

              <div class="col-md-12">
                <label class="form-label">Supporting Documents</label>
                <input
                    type="file"
                    class="form-control"
                    multiple
                    ref="documentFiles"
                    @change="handleFileSelection"
                >
                <small class="text-muted">Witness statements, evidence, relevant policies, etc.</small>
              </div>

              <!-- Display existing documents when editing -->
              <div v-if="warningForm.documentsList && warningForm.documentsList.length > 0" class="col-md-12">
                <label class="form-label">Existing Documents</label>
                <div class="list-group">
                  <div v-for="(doc, index) in warningForm.documentsList" :key="index" class="list-group-item list-group-item-action d-flex justify-content-between align-items-center">
                    <div>
                      <i class="mdi mdi-file-document me-2"></i>
                      {{ doc.name }}
                    </div>
                    <button
                        type="button"
                        class="btn btn-sm btn-danger"
                        @click="removeDocument(doc, index)"
                    >
                      <i class="mdi mdi-delete"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          <button type="button" class="btn btn-primary" @click="submitWarning">
            {{ isEditMode ? 'Update Warning' : 'Issue Warning' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Warning Details Modal -->
  <div class="modal fade" id="warningDetailsModal" tabindex="-1" aria-labelledby="warningDetailsModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="warningDetailsModalLabel">Warning Details</h5>


          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>


        </div>
        <div class="modal-body">
          <div v-if="selectedWarning">
            <div class="row mb-4">
              <div class="col-md-8">
                <h4 class="font-size-16">{{ selectedWarning.employee_name }}</h4>
                <p class="text-muted mb-1">{{ selectedWarning.warning_code }}</p>
                <div class="d-flex align-items-center my-2">
                  <span class="badge me-2" :class="getWarningTypeColor(selectedWarning.warning_type)">
                    {{ selectedWarning.warning_type }}
                  </span>

                  <span class="badge me-2" :class="getSeverityBadgeClass(selectedWarning.severity)">
                    {{ selectedWarning.severity }}
                  </span>
                  <span class="badge" :class="getStatusBadgeClass(selectedWarning.status)">
                    {{ selectedWarning.status }}
                  </span>
                </div>
                <p v-if="selectedWarning.department_name" class="text-muted mb-0">
                  <i class="mdi mdi-office-building me-1"></i> {{ selectedWarning.department_name }}
                </p>
              </div>
              <div class="col-md-4 text-end">
                <div class="mb-2">
                  <span class="badge bg-light text-dark">Issued: {{ formatDate(selectedWarning.warning_date) }}</span>
                </div>
                <div v-if="selectedWarning.next_review_date" class="mb-2">
                  <span class="badge bg-light text-dark">Next Review: {{ formatDate(selectedWarning.next_review_date) }}</span>
                </div>
                <div v-if="selectedWarning.expiry_date" class="mb-2">
                  <span class="badge bg-light text-dark">Expires: {{ formatDate(selectedWarning.expiry_date) }}</span>
                </div>
              </div>
            </div>

            <div class="row mb-4">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light py-2">
                    <h6 class="card-title mb-0">Incident Details</h6>
                  </div>
                  <div class="card-body">
                    <div class="mb-3">
                      <h6 class="font-size-14">Description</h6>
                      <p>{{ selectedWarning.description }}</p>
                    </div>
                    <div v-if="selectedWarning.recommended_action" class="mb-3">
                      <h6 class="font-size-14">Recommended Action</h6>
                      <p>{{ selectedWarning.recommended_action }}</p>
                    </div>
                    <div v-if="selectedWarning.comments" class="mb-3">
                      <h6 class="font-size-14">Additional Comments</h6>
                      <p>{{ selectedWarning.comments }}</p>
                    </div>
                    <div class="d-flex">
                      <div class="me-4">
                        <span class="d-block text-muted mb-1">Incident Date</span>
                        <span>{{ formatDate(selectedWarning.incident_date) }}</span>
                      </div>
                      <div class="me-4">
                        <span class="d-block text-muted mb-1">Issued By</span>
                        <span>{{ selectedWarning.issued_by }}</span>
                      </div>
                      <div v-if="selectedWarning.acknowledgment_required">
                        <span class="d-block text-muted mb-1">Acknowledgment</span>
                        <span class="badge" :class="selectedWarning.acknowledged ? 'bg-success' : 'bg-warning'">
                          {{ selectedWarning.acknowledged ? 'Acknowledged' : 'Pending Acknowledgment' }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Supporting Documents -->
            <div v-if="selectedWarning.documentsList && selectedWarning.documentsList.length > 0" class="row mb-4">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light py-2">
                    <h6 class="card-title mb-0">Supporting Documents</h6>
                  </div>
                  <div class="card-body">
                    <div class="list-group">
                      <div v-for="(doc, index) in selectedWarning.documentsList" :key="index" class="list-group-item list-group-item-action d-flex align-items-center">
                        <i class="mdi mdi-file-document me-2 font-size-18"></i>
                        <div class="flex-grow-1">
                          <h6 class="mb-1">{{ doc.name }}</h6>
                          <small class="text-muted">{{ doc.size }} - Uploaded {{ formatDateTime(doc.uploadedAt) }}</small>
                        </div>
                        <a :href="doc.path" class="btn btn-sm btn-primary" download>
                          <i class="mdi mdi-download me-1"></i> Download
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Responses/Acknowledgments -->
            <div v-if="selectedWarning.responses && selectedWarning.responses.length > 0" class="row mb-4">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light py-2">
                    <h6 class="card-title mb-0">Responses & Acknowledgments</h6>
                  </div>
                  <div class="card-body">
                    <div v-for="(response, index) in selectedWarning.responses" :key="index" class="warning-response mb-3">
                      <div class="warning-response-header">
                        <div>
                          <span class="badge me-2" :class="response.response_type === 'Acknowledgment' ? 'bg-success' : 'bg-info'">
                            {{ response.response_type }}
                          </span>
                          <span class="font-size-14 fw-medium">{{ response.response_by }}</span>
                        </div>
                        <small class="text-muted">{{ formatDateTime(response.response_date) }}</small>
                      </div>
                      <p class="mb-0">{{ response.response_text }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Activity Timeline -->
            <div v-if="selectedWarning.activities && selectedWarning.activities.length > 0" class="row">
              <div class="col-12">
                <div class="card">
                  <div class="card-header bg-light py-2">
                    <h6 class="card-title mb-0">Activity History</h6>
                  </div>
                  <div class="card-body">
                    <div class="warning-activity-timeline position-relative">
                      <div v-for="(activity, index) in selectedWarning.activities" :key="index" class="activity-item position-relative ps-5 pb-4">
                        <div class="activity-point position-absolute" style="left: 0; top: 0;">
                          <div :class="['activity-icon rounded-circle d-flex align-items-center justify-content-center', getActivityTypeClass(activity.activity_type)]" style="width: 36px; height: 36px;">
                            <i :class="['mdi', getActivityTypeIcon(activity.activity_type)]"></i>
                          </div>
                        </div>
                        <div class="activity-content">
                          <div class="d-flex justify-content-between mb-1">
                            <h6 class="font-size-14 mb-0">{{ activity.title }}</h6>
                            <small class="text-muted">{{ formatDateTime(activity.timestamp) }}</small>
                          </div>
                          <p class="text-muted mb-0">{{ activity.description }}</p>
                          <small class="text-muted">By: {{ activity.user }}</small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-success" @click="exportWarningDetails(selectedWarning)">
            <i class="mdi mdi-file-export me-1"></i> Export PDF
          </button>

          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          <button
              v-if="selectedWarning && selectedWarning.status === 'Active'"
              type="button"
              class="btn btn-primary"
              @click="showAddResponseModal"
          >
            <i class="mdi mdi-message-reply me-1"></i> Add Response
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Acknowledge Warning Modal -->
  <div class="modal fade" id="acknowledgeModal" tabindex="-1" aria-labelledby="acknowledgeModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="acknowledgeModalLabel">Acknowledge Warning</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="selectedWarning">
            <p>You are acknowledging the following warning:</p>
            <div class="alert alert-info">
              <h5 class="alert-heading">{{ selectedWarning.warning_type }} - {{ selectedWarning.warning_code }}</h5>
              <p class="mb-0"><strong>Employee:</strong> {{ selectedWarning.employee_name }}</p>
              <p class="mb-0"><strong>Issued:</strong> {{ formatDate(selectedWarning.warning_date) }}</p>
              <hr>
              <p class="mb-0">{{ selectedWarning.description }}</p>
            </div>
            <div class="mb-3">
              <label class="form-label">Response (Optional)</label>
              <textarea v-model="acknowledgeForm.responseText" class="form-control" rows="4" placeholder="You may provide optional comments or feedback about this warning..."></textarea>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button type="button" class="btn btn-success" @click="confirmAcknowledgeWarning">
            <i class="mdi mdi-check me-1"></i> Acknowledge Warning
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Resolve Warning Modal -->
  <div class="modal fade" id="resolveModal" tabindex="-1" aria-labelledby="resolveModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="resolveModalLabel">Resolve Warning</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="selectedWarning">
            <p>You are resolving the following warning:</p>
            <div class="alert alert-warning">
              <h5 class="alert-heading">{{ selectedWarning.warning_type }} - {{ selectedWarning.warning_code }}</h5>
              <p class="mb-0"><strong>Employee:</strong> {{ selectedWarning.employee_name }}</p>
              <p class="mb-0"><strong>Issued:</strong> {{ formatDate(selectedWarning.warning_date) }}</p>
              <hr>
              <p class="mb-0">{{ selectedWarning.description }}</p>
            </div>
            <div class="mb-3">
              <label class="form-label">Resolution Notes <span class="text-danger">*</span></label>
              <textarea v-model="resolveForm.resolutionNotes" class="form-control" rows="4" placeholder="Provide details on why this warning is being resolved..." required></textarea>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button type="button" class="btn btn-success" @click="confirmResolveWarning">
            <i class="mdi mdi-check-all me-1"></i> Resolve Warning
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Add Response Modal -->
  <div class="modal fade" id="addResponseModal" tabindex="-1" aria-labelledby="addResponseModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="addResponseModalLabel">Add Response</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="selectedWarning">
            <div class="mb-3">
              <label class="form-label">Response Type</label>
              <select v-model="responseForm.responseType" class="form-select">
                <option value="Comment">Comment</option>
                <option value="Follow-up">Follow-up</option>
                <option value="Feedback">Feedback</option>
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label">Response Text <span class="text-danger">*</span></label>
              <textarea v-model="responseForm.responseText" class="form-control" rows="4" required></textarea>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button type="button" class="btn btn-primary" @click="addResponse">
            <i class="mdi mdi-send me-1"></i> Submit Response
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { toast, authUrl, execute } from "@/api";
import _ from 'lodash';
import NavigationBar from "@/components/Navigation.vue";
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';

export default {
  name: "HRWarnings",
  components: {NavigationBar},
  data() {
    return {
      submitLoading: false,
      loading: false,
      warnings: [],
      recentWarnings: [],
      departments: [],
      employees: [],
      warningTypes: ['Performance Issue', 'Attendance', 'Misconduct', 'Policy Violation', 'Safety Violation', 'Insubordination', 'Unprofessional Behavior', 'Ethics Violation', 'Other'],
      filters: {
        departmentId: "",
        warningType: "",
        severity: "",
        status: "",
        startDate: this.getDateOffset(-90), // Last 90 days
        endDate: this.getDateOffset(90), // Next 90 days
        search: "",
        page: 1,
        limit: 10
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalWarnings: 0,
        limit: 10
      },
      metrics: {
        totalWarnings: 0,
        highSeverity: 0,
        mediumSeverity: 0,
        lowSeverity: 0,
        activeWarnings: 0,
        resolvedWarnings: 0,
        expiredWarnings: 0,
        pendingAcknowledgment: 0,
        issuedToday: 0,
        upcomingReviews: 0,
        typeCounts: {}
      },
      selectedWarning: null,
      warningForm: {
        employeeId: "",
        warningType: "",
        warningDate: new Date().toISOString().split('T')[0],
        incidentDate: new Date().toISOString().split('T')[0],
        severity: "Medium",
        description: "",
        recommendedAction: "",
        acknowledgmentRequired: true,
        status: "Active",
        nextReviewDate: "",
        expiryDate: "",
        comments: "",
        documentsList: []
      },
      isEditMode: false,
      selectedEmployee: null,
      removedDocuments: [],
      pendingFiles: [],
      acknowledgeForm: {
        responseText: ""
      },
      resolveForm: {
        resolutionNotes: ""
      },
      responseForm: {
        responseType: "Comment",
        responseText: ""
      }
    };
  },
  mounted() {
    this.getDepartments();
    this.getWarnings();
    this.getWarningTypes();
  },
  methods: {
    getDateOffset(days) {
      const date = new Date();
      date.setDate(date.getDate() + days);
      return date.toISOString().split('T')[0];
    },

    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.departments = res.data.data.departments || [];
            } else {
              console.error("Error fetching departments:", res.data.message);
              this.showToast("error", "Failed to load departments");
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching departments");
          });
    },

    getWarnings() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getWarnings");
      data.append("startDate", this.filters.startDate);
      data.append("endDate", this.filters.endDate);
      data.append("page", this.filters.page);
      data.append("limit", this.filters.limit);

      if (this.filters.departmentId) {
        data.append("departmentId", this.filters.departmentId);
      }

      if (this.filters.warningType) {
        data.append("warningType", this.filters.warningType);
      }

      if (this.filters.severity) {
        data.append("severity", this.filters.severity);
      }

      if (this.filters.status) {
        data.append("status", this.filters.status);
      }

      if (this.filters.search) {
        data.append("search", this.filters.search);
      }

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.warnings = res.data.data.warnings || [];
              this.metrics = res.data.data.metrics || {};
              this.recentWarnings = res.data.data.recentWarnings || [];
              this.pagination = res.data.data.pagination || {
                currentPage: 1,
                totalPages: 1,
                totalWarnings: 0,
                limit: 10
              };
            } else {
              console.error("Error fetching warnings:", res.data.message);
              this.showToast("error", "Failed to load warnings data: " + res.data.message);
              this.warnings = [];
              this.metrics = {};
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching warnings");
            this.warnings = [];
            this.metrics = {};
          });
    },

    getWarningTypes() {
      const data = new FormData();
      data.append("function", "getWarningTypes");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.warningTypes = res.data.data.warningTypes || this.warningTypes;
            } else {
              console.error("Error fetching warning types:", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
          });
    },

    getEmployees() {
      const data = new FormData();
      data.append("function", "getEmployees");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.employees = res.data.data.employees || [];
            } else {
              console.error("Error fetching employees:", res.data.message);
              this.showToast("error", "Failed to load employees");
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching employees");
          });
    },

    refreshData() {
      this.getWarnings();
    },

    resetFilters() {
      this.filters = {
        departmentId: "",
        warningType: "",
        severity: "",
        status: "",
        startDate: this.getDateOffset(-90),
        endDate: this.getDateOffset(90),
        search: "",
        page: 1,
        limit: 10
      };
      this.getWarnings();
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.totalPages || page === this.filters.page) {
        return;
      }

      this.filters.page = page;
      this.getWarnings();
    },

    getPageNumbers() {
      const totalPages = this.pagination.totalPages || 1;
      const currentPage = this.pagination.currentPage || 1;

      if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }

      const pages = [1];

      if (currentPage <= 3) {
        pages.push(2, 3);
        if (totalPages > 3) pages.push('...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push('...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push('...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }

      return pages;
    },

    createWarning() {
      this.isEditMode = false;
      this.warningForm = {
        employeeId: "",
        warningType: "",
        warningDate: new Date().toISOString().split('T')[0],
        incidentDate: new Date().toISOString().split('T')[0],
        severity: "Medium",
        description: "",
        recommendedAction: "",
        acknowledgmentRequired: true,
        status: "Active",
        nextReviewDate: "",
        expiryDate: "",
        comments: "",
        documentsList: []
      };

      this.selectedEmployee = null;
      this.removedDocuments = [];
      this.pendingFiles = [];

      // Get employees for selection
      this.getEmployees();

      new bootstrap.Modal(document.getElementById('warningModal')).show();
    },

    editWarning(warning) {
      this.isEditMode = true;
      this.selectedWarning = warning;

      // Prepare form data
      this.warningForm = {
        warningId: warning.id,
        employeeId: warning.employee_id,
        warningType: warning.warning_type,
        warningDate: warning.warning_date,
        incidentDate: warning.incident_date,
        severity: warning.severity,
        description: warning.description,
        recommendedAction: warning.recommended_action,
        acknowledgmentRequired: Boolean(warning.acknowledgment_required),
        status: warning.status,
        nextReviewDate: warning.next_review_date,
        expiryDate: warning.expiry_date,
        comments: warning.comments,
        documentsList: warning.documentsList || []
      };

      // Get employee details
      this.selectedEmployee = {
        id: warning.employee_id,
        firstName: warning.employee_name.split(' ')[0],
        lastName: warning.employee_name.split(' ').slice(1).join(' '),
        departmentName: warning.department_name,
        designation: warning.designation || "Employee"
      };

      this.removedDocuments = [];
      this.pendingFiles = [];

      // Open the modal
      new bootstrap.Modal(document.getElementById('warningModal')).show();
    },

    handleFileSelection(event) {
      this.pendingFiles = event.target.files;
    },

    removeDocument(doc, index) {
      this.removedDocuments.push(doc.path);
      this.warningForm.documentsList.splice(index, 1);
    },

    async submitWarning() {
      // Validate form
      if (!this.validateWarningForm()) {
        return;
      }

      this.submitLoading = true;
      const data = new FormData();

      if (this.isEditMode) {
        data.append("function", "updateWarning");
        data.append("warningId", this.warningForm.warningId);
      } else {
        data.append("function", "createWarning");
      }

      // Add all form fields
      data.append("employeeId", this.warningForm.employeeId);
      data.append("warningType", this.warningForm.warningType);
      data.append("warningDate", this.warningForm.warningDate);
      data.append("incidentDate", this.warningForm.incidentDate);
      data.append("severity", this.warningForm.severity);
      data.append("description", this.warningForm.description);
      data.append("recommendedAction", this.warningForm.recommendedAction);
      data.append("acknowledgmentRequired", this.warningForm.acknowledgmentRequired ? 1 : 0);
      data.append("status", this.warningForm.status);

      if (this.warningForm.nextReviewDate) {
        data.append("nextReviewDate", this.warningForm.nextReviewDate);
      }

      if (this.warningForm.expiryDate) {
        data.append("expiryDate", this.warningForm.expiryDate);
      }

      data.append("comments", this.warningForm.comments);

      // Handle file uploads
      if (this.pendingFiles.length > 0) {
        for (let i = 0; i < this.pendingFiles.length; i++) {
          data.append(`document_${i}`, this.pendingFiles[i]);
        }
      }

      // Handle document removals
      if (this.isEditMode && this.removedDocuments.length > 0) {
        data.append("removedDocuments", JSON.stringify(this.removedDocuments));
      }

      // Submit the form
      execute(data, authUrl)
          .then((res) => {
            this.submitLoading = false;
            if (res.data.success) {
              // Close the modal
              const modal = bootstrap.Modal.getInstance(document.getElementById('warningModal'));
              if (modal) {
                modal.hide();
              }

              this.showToast("success", res.data.message);
              this.getWarnings(); // Refresh the data
            } else {
              this.showToast("error", res.data.message);
            }
          })
          .catch((error) => {
            this.submitLoading = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when saving warning");
          });
    },

    validateWarningForm() {
      // Basic validation
      if (!this.isEditMode && !this.warningForm.employeeId) {
        this.showToast("error", "Please select an employee");
        return false;
      }

      if (!this.warningForm.warningType) {
        this.showToast("error", "Warning type is required");
        return false;
      }

      if (!this.warningForm.severity) {
        this.showToast("error", "Severity is required");
        return false;
      }

      if (!this.warningForm.description) {
        this.showToast("error", "Description is required");
        return false;
      }

      return true;
    },

    viewWarningDetails(warning) {
      this.selectedWarning = null;
      this.getWarningDetails(warning.id);
    },

    acknowledgeWarning(warning) {
      this.selectedWarning = warning;
      this.acknowledgeForm = {
        responseText: ""
      };

      // Show acknowledgment modal
      new bootstrap.Modal(document.getElementById('acknowledgeModal')).show();
    },

    resolveWarning(warning) {
      this.selectedWarning = warning;
      this.resolveForm = {
        resolutionNotes: ""
      };

      // Show resolve modal
      new bootstrap.Modal(document.getElementById('resolveModal')).show();
    },

    showAddResponseModal() {
      this.responseForm = {
        responseType: "Comment",
        responseText: ""
      };

      // Close the details modal
      const detailsModal = bootstrap.Modal.getInstance(document.getElementById('warningDetailsModal'));
      if (detailsModal) {
        detailsModal.hide();
      }

      // Show the add response modal
      setTimeout(() => {
        new bootstrap.Modal(document.getElementById('addResponseModal')).show();
      }, 500);
    },

    addResponse() {
      if (!this.responseForm.responseText) {
        this.showToast("error", "Response text is required");
        return;
      }

      const data = new FormData();
      data.append("function", "addWarningResponse");
      data.append("warningId", this.selectedWarning.id);
      data.append("responseType", this.responseForm.responseType);
      data.append("responseText", this.responseForm.responseText);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              // Close the modal
              const modal = bootstrap.Modal.getInstance(document.getElementById('addResponseModal'));
              if (modal) {
                modal.hide();
              }

              this.showToast("success", res.data.message);
              this.responseForm.responseText = "";

              // Reopen the details modal and refresh the warning details
              setTimeout(() => {
                this.getWarningDetails(this.selectedWarning.id);
              }, 500);
            } else {
              this.showToast("error", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when adding response");
          });
    },

    getWarningDetails(warningId) {
      const data = new FormData();
      data.append("function", "getWarningDetails");
      data.append("warningId", warningId);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.selectedWarning = res.data.data.warning;
              // Open the modal
              new bootstrap.Modal(document.getElementById('warningDetailsModal')).show();

            } else {
              this.showToast("error", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching warning details");
          });
    },

    confirmAcknowledgeWarning() {
      const data = new FormData();
      data.append("function", "acknowledgeWarning");
      data.append("warningId", this.selectedWarning.id);
      data.append("responseText", this.acknowledgeForm.responseText);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              // Close the modal
              const modal = bootstrap.Modal.getInstance(document.getElementById('acknowledgeModal'));
              if (modal) {
                modal.hide();
              }

              this.showToast("success", res.data.message);
              this.getWarnings(); // Refresh the data

              // If warning details modal is open, refresh it
              if (document.getElementById('warningDetailsModal').classList.contains('show')) {
                this.getWarningDetails(this.selectedWarning.id);
              }
            } else {
              this.showToast("error", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when acknowledging warning");
          });
    },

    confirmResolveWarning() {
      if (!this.resolveForm.resolutionNotes) {
        this.showToast("error", "Resolution notes are required");
        return;
      }

      const data = new FormData();
      data.append("function", "resolveWarning");
      data.append("warningId", this.selectedWarning.id);
      data.append("resolutionNotes", this.resolveForm.resolutionNotes);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              // Close the modal
              const modal = bootstrap.Modal.getInstance(document.getElementById('resolveModal'));
              if (modal) {
                modal.hide();
              }

              this.showToast("success", res.data.message);
              this.getWarnings(); // Refresh the data

              // If warning details modal is open, refresh it
              if (document.getElementById('warningDetailsModal').classList.contains('show')) {
                this.getWarningDetails(this.selectedWarning.id);
              }
            } else {
              this.showToast("error", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when resolving warning");
          });
    },

    exportWarnings() {
      // Instead of sending a request to the server, we'll export the data we already have
      try {
        // First, determine if we should export filtered data or all data
        this.loading = true;

        // Get all warnings if we need to export everything
        // This is only necessary if the current view is filtered
        const isFiltered = this.filters.departmentId || this.filters.warningType ||
            this.filters.severity || this.filters.status || this.filters.search;

        if (isFiltered) {
          // Create a copy of current filters but remove pagination
          const exportFilters = {...this.filters, page: 1, limit: 1000};

          const data = new FormData();
          data.append("function", "getWarnings");
          data.append("startDate", exportFilters.startDate);
          data.append("endDate", exportFilters.endDate);
          data.append("page", exportFilters.page);
          data.append("limit", exportFilters.limit);

          if (exportFilters.departmentId) {
            data.append("departmentId", exportFilters.departmentId);
          }

          if (exportFilters.warningType) {
            data.append("warningType", exportFilters.warningType);
          }

          if (exportFilters.severity) {
            data.append("severity", exportFilters.severity);
          }

          if (exportFilters.status) {
            data.append("status", exportFilters.status);
          }

          if (exportFilters.search) {
            data.append("search", exportFilters.search);
          }

          // Fetch all data for export
          execute(data, authUrl)
              .then((res) => {
                this.loading = false;
                if (res.data.success) {
                  const warningsToExport = res.data.data.warnings || [];
                  this.generateCSV(warningsToExport);
                } else {
                  this.showToast("error", "Failed to fetch data for export: " + res.data.message);
                }
              })
              .catch((error) => {
                this.loading = false;
                console.error("API error:", error);
                this.showToast("error", "Network error when fetching data for export");
              });
        } else {
          // Use the warnings we already have if there are no filters
          this.generateCSV(this.warnings);
          this.loading = false;
        }
      } catch (error) {
        this.loading = false;
        console.error("Export error:", error);
        this.showToast("error", "Error exporting warnings: " + error.message);
      }
    },
    exportWarningDetails(warning) {
      if (!warning) {
        // Use selected warning if no specific warning is provided
        warning = this.selectedWarning;
      }

      if (!warning) {
        this.showToast("error", "No warning selected for export");
        return;
      }

      try {
        // Fetch complete details for the warning if needed
        if (!warning.responses || !warning.activities) {
          this.loading = true;

          const data = new FormData();
          data.append("function", "getWarningDetails");
          data.append("warningId", warning.id);

          execute(data, authUrl)
              .then((res) => {
                this.loading = false;
                if (res.data.success) {
                  const warningDetails = res.data.data.warning;
                  this.generateWarningDetailsPDF(warningDetails);
                } else {
                  this.showToast("error", "Failed to fetch warning details: " + res.data.message);
                }
              })
              .catch((error) => {
                this.loading = false;
                console.error("API error:", error);
                this.showToast("error", "Network error when fetching warning details");
              });
        } else {
          // We already have complete warning details
          this.generateWarningDetailsPDF(warning);
        }
      } catch (error) {
        this.loading = false;
        console.error("Export error:", error);
        this.showToast("error", "Error exporting warning details: " + error.message);
      }
    },

    generateWarningDetailsPDF(warning) {
      if (!warning) {
        this.showToast("error", "No warning details to export");
        return;
      }

      // Import jsPDF and autoTable at the top of your component file:
      // import { jsPDF } from "jspdf";
      // import autoTable from 'jspdf-autotable';

      try {
        // Create new PDF document
        const doc = new jsPDF();

        // Add company logo (if available)
        // doc.addImage("logo.png", "PNG", 14, 10, 30, 15);

        // Document title
        doc.setFontSize(20);
        doc.setTextColor(40, 40, 40);
        doc.text("EMPLOYEE WARNING NOTICE", 105, 20, { align: "center" });

        // Warning reference
        doc.setFontSize(12);
        doc.text(`Reference: ${warning.warning_code || 'N/A'}`, 14, 30);

        // Set status badge
        let statusColor;
        switch(warning.status) {
          case 'Active': statusColor = [255, 153, 0]; break; // Orange
          case 'Resolved': statusColor = [0, 153, 0]; break; // Green
          case 'Expired': statusColor = [128, 128, 128]; break; // Gray
          default: statusColor = [0, 102, 204]; // Blue
        }

        // Status badge
        doc.setFillColor(...statusColor);
        doc.roundedRect(160, 25, 35, 10, 2, 2, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(10);
        doc.text(warning.status || 'N/A', 177.5, 31, { align: "center" });

        // Reset text color for remaining content
        doc.setTextColor(40, 40, 40);

        // Employee section
        doc.setFontSize(16);
        doc.text("Employee Information", 14, 45);
        doc.setDrawColor(220, 220, 220);
        doc.line(14, 47, 196, 47);

        // Employee details table
        autoTable(doc, {
          startY: 50,
          head: [['Employee Name', 'Department', 'Position']],
          body: [[
            warning.employee_name || 'N/A',
            warning.department_name || 'N/A',
            warning.designation || 'N/A'
          ]],
          theme: 'grid',
          headStyles: { fillColor: [60, 60, 60], textColor: [255, 255, 255] },
          styles: { fontSize: 10 }
        });

        // Warning details section
        doc.setFontSize(16);
        doc.text("Warning Details", 14, doc.lastAutoTable.finalY + 15);
        doc.setDrawColor(220, 220, 220);
        doc.line(14, doc.lastAutoTable.finalY + 17, 196, doc.lastAutoTable.finalY + 17);

        // Warning metadata
        const warningData = [
          [
            { content: 'Warning Type', styles: { fontStyle: 'bold' } },
            warning.warning_type || 'N/A',
            { content: 'Severity', styles: { fontStyle: 'bold' } },
            warning.severity || 'N/A'
          ],
          [
            { content: 'Warning Date', styles: { fontStyle: 'bold' } },
            warning.warning_date ? this.formatDate(warning.warning_date) : 'N/A',
            { content: 'Incident Date', styles: { fontStyle: 'bold' } },
            warning.incident_date ? this.formatDate(warning.incident_date) : 'N/A'
          ],
          [
            { content: 'Next Review', styles: { fontStyle: 'bold' } },
            warning.next_review_date ? this.formatDate(warning.next_review_date) : 'N/A',
            { content: 'Expiry Date', styles: { fontStyle: 'bold' } },
            warning.expiry_date ? this.formatDate(warning.expiry_date) : 'N/A'
          ],
          [
            { content: 'Acknowledgment Required', styles: { fontStyle: 'bold' } },
            warning.acknowledgment_required ? 'Yes' : 'No',
            { content: 'Acknowledgment Status', styles: { fontStyle: 'bold' } },
            warning.acknowledgment_required ?
                (warning.acknowledged ? 'Acknowledged' : 'Pending Acknowledgment') :
                'Not Required'
          ],
          [
            { content: 'Issued By', styles: { fontStyle: 'bold' } },
            warning.issued_by || 'N/A',
            { content: '', styles: { fontStyle: 'bold' } },
            ''
          ]
        ];

        autoTable(doc, {
          startY: doc.lastAutoTable.finalY + 20,
          body: warningData,
          theme: 'plain',
          styles: { fontSize: 10, cellPadding: 3 },
          columnStyles: {
            0: { cellWidth: 40 },
            1: { cellWidth: 45 },
            2: { cellWidth: 40 },
            3: { cellWidth: 45 }
          }
        });

        // Description section
        doc.setFontSize(12);
        doc.text("Description of Offense", 14, doc.lastAutoTable.finalY + 10);

        autoTable(doc, {
          startY: doc.lastAutoTable.finalY + 15,
          body: [[warning.description || 'No description provided.']],
          theme: 'plain',
          styles: { fontSize: 10, cellPadding: 5 },
          columnStyles: { 0: { cellWidth: 180 } }
        });

        // Recommended action section
        doc.setFontSize(12);
        doc.text("Recommended Action", 14, doc.lastAutoTable.finalY + 10);

        autoTable(doc, {
          startY: doc.lastAutoTable.finalY + 15,
          body: [[warning.recommended_action || 'No recommended action provided.']],
          theme: 'plain',
          styles: { fontSize: 10, cellPadding: 5 },
          columnStyles: { 0: { cellWidth: 180 } }
        });

        // Additional comments section
        if (warning.comments) {
          doc.setFontSize(12);
          doc.text("Additional Comments", 14, doc.lastAutoTable.finalY + 10);

          autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 15,
            body: [[warning.comments]],
            theme: 'plain',
            styles: { fontSize: 10, cellPadding: 5 },
            columnStyles: { 0: { cellWidth: 180 } }
          });
        }

        // Check if we need to add a new page for responses and activities
        if ((warning.responses && warning.responses.length > 0) ||
            (warning.activities && warning.activities.length > 0)) {

          // Add a new page if we're getting close to the bottom
          if (doc.lastAutoTable.finalY > 230) {
            doc.addPage();
          } else {
            // Add some spacing
            doc.setFontSize(16);
            doc.text("Responses & Activity History", 14, doc.lastAutoTable.finalY + 20);
            doc.setDrawColor(220, 220, 220);
            doc.line(14, doc.lastAutoTable.finalY + 22, 196, doc.lastAutoTable.finalY + 22);
          }

          // Add responses if available
          if (warning.responses && warning.responses.length > 0) {
            doc.setFontSize(12);
            doc.text("Responses & Acknowledgments", 14, doc.lastAutoTable.finalY + 30);

            const responseData = warning.responses.map(response => [
              response.response_type || 'N/A',
              response.response_by || 'N/A',
              this.formatDateTime(response.response_date) || 'N/A',
              response.response_text || 'N/A'
            ]);

            autoTable(doc, {
              startY: doc.lastAutoTable.finalY + 35,
              head: [['Type', 'By', 'Date/Time', 'Response']],
              body: responseData,
              theme: 'striped',
              headStyles: { fillColor: [80, 80, 80] },
              styles: { fontSize: 9, overflow: 'linebreak' },
              columnStyles: {
                0: { cellWidth: 25 },
                1: { cellWidth: 35 },
                2: { cellWidth: 35 },
                3: { cellWidth: 85 }
              }
            });
          }

          // Add activity history if available
          if (warning.activities && warning.activities.length > 0) {
            // Add a new page if we're getting close to the bottom
            if (doc.lastAutoTable.finalY > 200) {
              doc.addPage();
              doc.setFontSize(12);
              doc.text("Activity History", 14, 20);
            } else {
              doc.setFontSize(12);
              doc.text("Activity History", 14, doc.lastAutoTable.finalY + 15);
            }

            const activityData = warning.activities.map(activity => [
              this.formatDateTime(activity.timestamp) || 'N/A',
              activity.title || 'N/A',
              activity.user || 'N/A',
              activity.description || 'N/A'
            ]);

            autoTable(doc, {
              startY: doc.lastAutoTable.finalY + 20,
              head: [['Date/Time', 'Activity', 'User', 'Details']],
              body: activityData,
              theme: 'striped',
              headStyles: { fillColor: [80, 80, 80] },
              styles: { fontSize: 9, overflow: 'linebreak' },
              columnStyles: {
                0: { cellWidth: 35 },
                1: { cellWidth: 35 },
                2: { cellWidth: 30 },
                3: { cellWidth: 80 }
              }
            });
          }
        }

        // Add footer with page numbers
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(8);
          doc.setTextColor(100, 100, 100);

          // Current date at bottom left
          doc.text(
              `Generated: ${new Date().toLocaleString()}`,
              14,
              doc.internal.pageSize.height - 10
          );

          // Page numbers at bottom right
          doc.text(
              `Page ${i} of ${pageCount}`,
              doc.internal.pageSize.width - 14,
              doc.internal.pageSize.height - 10,
              { align: 'right' }
          );
        }

        // Save the PDF file
        const filename = `warning-${warning.warning_code || 'detail'}-${warning.employee_name ? warning.employee_name.replace(/\s+/g, '-') : 'employee'}.pdf`;
        doc.save(filename);

        this.showToast("success", "Warning details exported as PDF successfully");
      } catch (error) {
        console.error("PDF generation error:", error);
        this.showToast("error", "Error generating PDF: " + error.message);
      }
    },
    generateCSV(warningsData) {
      if (!warningsData || warningsData.length === 0) {
        this.showToast("warning", "No warnings data to export");
        return;
      }

      // Define the CSV columns
      const csvColumns = [
        "Warning Code",
        "Employee Name",
        "Department",
        "Warning Type",
        "Severity",
        "Warning Date",
        "Incident Date",
        "Status",
        "Acknowledgment Status",
        "Description"
      ];

      // Convert warnings data to CSV rows
      const csvRows = warningsData.map(warning => [
        warning.warning_code || "",
        warning.employee_name || "",
        warning.department_name || "",
        warning.warning_type || "",
        warning.severity || "",
        warning.warning_date ? this.formatDate(warning.warning_date) : "",
        warning.incident_date ? this.formatDate(warning.incident_date) : "",
        warning.status || "",
        warning.acknowledgment_required
            ? (warning.acknowledged ? "Acknowledged" : "Pending Acknowledgment")
            : "Not Required",
        warning.description ? `"${warning.description.replace(/"/g, '""')}"` : ""
      ]);

      // Create CSV content
      let csvContent = [csvColumns, ...csvRows]
          .map(row => row.join(","))
          .join("\n");

      // Create and download the CSV file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      const today = new Date();
      const formattedDate = today.toISOString().split("T")[0];
      const filename = `employee-warnings-${formattedDate}.csv`;

      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.showToast("success", "Warnings data exported successfully");
    },

    // Permission checks
    canEditWarning(warning) {
      // You can add additional permission checks here if needed
      return warning.status === 'Active';
    },

    canAcknowledgeWarning(warning) {
      return warning.status === 'Active' &&
          warning.acknowledgment_required &&
          !warning.acknowledged;
    },

    canResolveWarning(warning) {
      // You can add additional permission checks here if needed
      return warning.status === 'Active';
    },

    // Utility methods
    formatDate(dateString) {
      if (!dateString) return 'N/A';

      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return 'N/A';

      const date = new Date(dateTimeString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatDateMonth(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short' });
    },

    formatDateDay(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      return date.getDate();
    },

    getWarningTypeColor(type) {
      if (!type) return 'bg-secondary';

      const typeMap = {
        'Performance Issue': 'bg-primary',
        'Attendance': 'bg-warning',
        'Misconduct': 'bg-danger',
        'Policy Violation': 'bg-info',
        'Safety Violation': 'bg-danger',
        'Insubordination': 'bg-dark',
        'Unprofessional Behavior': 'bg-purple',
        'Ethics Violation': 'bg-danger',
        'Other': 'bg-secondary'
      };

      return typeMap[type] || 'bg-secondary';
    },

    getSeverityBadgeClass(severity) {
      if (!severity) return 'bg-secondary';

      const severityMap = {
        'High': 'bg-danger',
        'Medium': 'bg-warning',
        'Low': 'bg-info'
      };

      return severityMap[severity] || 'bg-secondary';
    },

    getStatusBadgeClass(status) {
      if (!status) return 'bg-secondary';

      const statusMap = {
        'Active': 'bg-warning',
        'Resolved': 'bg-success',
        'Expired': 'bg-secondary'
      };

      return statusMap[status] || 'bg-secondary';
    },

    getWarningTypePercentage(type) {
      if (!this.metrics.typeCounts || !this.metrics.totalWarnings) return 0;

      const count = this.metrics.typeCounts[type] || 0;
      const total = Object.values(this.metrics.typeCounts).reduce((sum, count) => sum + count, 0);
      return ((count / total) * 100).toFixed(1);
    },

    getActivityTypeClass(type) {
      if (!type) return 'bg-secondary text-white';

      const typeMap = {
        'create': 'bg-primary text-white',
        'update': 'bg-info text-white',
        'acknowledge': 'bg-success text-white',
        'resolve': 'bg-success text-white',
        'response': 'bg-purple text-white'
      };

      return typeMap[type] || 'bg-secondary text-white';
    },

    getActivityTypeIcon(type) {
      if (!type) return 'mdi-help-circle';

      const typeMap = {
        'create': 'mdi-plus-circle',
        'update': 'mdi-pencil',
        'acknowledge': 'mdi-check-circle',
        'resolve': 'mdi-check-all',
        'response': 'mdi-message-reply'
      };

      return typeMap[type] || 'mdi-help-circle';
    },

    showToast(type, message) {
      if (type === 'success') {
        toast.success(message);
      } else if (type === 'error') {
        toast.error(message);
      } else if (type === 'warning') {
        toast.warning(message);
      } else {
        toast.info(message);
      }
    }
  },
  watch: {
    'warningForm.employeeId': {
      handler(employeeId) {
        if (!employeeId) {
          this.selectedEmployee = null;
          return;
        }

        // Find the selected employee from the employees list
        const employee = this.employees.find(emp => emp.id === employeeId);
        if (employee) {
          this.selectedEmployee = employee;
        }
      },
      immediate: true
    },
    'filters.departmentId': function() {
      this.filters.page = 1;
      this.getWarnings();
    },
    'filters.warningType': function() {
      this.filters.page = 1;
      this.getWarnings();
    },
    'filters.severity': function() {
      this.filters.page = 1;
      this.getWarnings();
    },
    'filters.status': function() {
      this.filters.page = 1;
      this.getWarnings();
    },
    'filters.search': _.debounce(function() {
      this.filters.page = 1;
      this.getWarnings();
    }, 500),
    'filters.startDate': function() {
      this.getWarnings();
    },
    'filters.endDate': function() {
      this.getWarnings();
    }
  }
};
</script>

<style scoped>
/* Card hover effects */
.card {
  transition: transform 0.3s, box-shadow 0.3s;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

/* Custom table styles */
.table th {
  font-weight: 600;
  color: #495057;
}

.table td {
  vertical-align: middle;
}

.warning-date-badge {
  min-width: 45px;
}

.date-month {
  text-transform: uppercase;
  font-size: 0.8rem;
  color: #6c757d;
}

/* Warning Type Colors */
.bg-purple {
  background-color: #6f42c1;
  color: white;
}

.approval-connector {
  width: 2px;
  height: 40px;
  margin-left: 19px;
  margin-top: 5px;
  margin-bottom: 5px;
}

/* Response styles */
.warning-response {
  position: relative;
  padding: 1rem;
  margin-bottom: 1rem;
  border-radius: 0.25rem;
  background-color: #f8f9fa;
}

.warning-response::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background-color: #6f42c1;
  border-radius: 4px 0 0 4px;
}

.warning-response-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.warning-activity-timeline .activity-item:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 36px;
  left: 18px;
  width: 2px;
  height: calc(100% - 36px);
  background-color: #dee2e6;
}
</style>